'use client'
import React, { ReactNode, useState } from 'react'
import { motion } from 'framer-motion'
import { Text } from '../../atoms'
import { TextTypes } from '../../../types'
import { ChevronDownIcon } from '../../icons'

export interface CollapsibleCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
    title?: ReactNode
    description?: string
    children?: React.ReactNode
}

export const CollapsibleCard = ({ title, description, children, ...props }: CollapsibleCardProps): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false)

    return (
        <div
            {...props}
            className={`list-card flex flex-col border border-border-default bg-primary ${props?.className || ''}`}
        >
            <button
                type="button"
                className="flex text-left items-center p-6"
                onClick={() => {
                    if (children) setIsExpanded((current) => !current)
                }}
                aria-expanded={children ? isExpanded : undefined}
            >
                <span className="inline-flex flex-col gap-1 grow">
                    {title && <Text type={TextTypes.HEADING_4}>{title}</Text>}
                    {description && <Text type={TextTypes.SMALL_TEXT}>{description}</Text>}
                </span>
                {children && (
                    <span className="shrink-0">
                        <ChevronDownIcon className={`transform transition-transform ${isExpanded && 'rotate-180'}`} />
                    </span>
                )}
            </button>
            <motion.div
                key="content"
                initial="collapsed"
                animate={children && isExpanded ? 'open' : 'collapsed'}
                exit="collapsed"
                variants={{
                    open: { opacity: 1, height: 'auto', visibility: 'visible' },
                    collapsed: { opacity: 0, height: 0, visibility: 'hidden' },
                }}
                transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
                className="list-card__content overflow-hidden"
            >
                <div className="px-6 pb-6 pt-0 w-full">
                    <div className="border-t border-border-default pt-4 w-full">{children}</div>
                </div>
            </motion.div>
        </div>
    )
}
